/**
 * ActivityDiff - Affichage des changements (old -> new)
 *
 * Formats supportes:
 * - { type: 'create', values: {...} } : creation avec nouvelles valeurs
 * - { type: 'delete', values: {...} } : suppression avec anciennes valeurs
 * - { type: 'update', changes: [{field, from, to}] } : modifications
 */
import React, { useState } from 'react';

// Labels lisibles pour les champs communs
const FIELD_LABELS = {
  status: 'Statut',
  name: 'Nom',
  code: 'Code',
  address: 'Adresse',
  city: 'Ville',
  postal_code: 'Code postal',
  phone: 'Telephone',
  email: 'Email',
  start_date: 'Date debut',
  end_date: 'Date fin',
  days_count: 'Nombre de jours',
  reason: 'Motif',
  is_half_day: 'Demi-journee',
  leave_type: 'Type de conge',
  distance_from_depot: 'Distance depot',
  notes: 'Notes',
  is_active: 'Actif',
  rejection_reason: 'Motif de rejet',
  validated_by: 'Valide par'
};

const formatValue = (value) => {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'boolean') return value ? 'Oui' : 'Non';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const getFieldLabel = (field) => FIELD_LABELS[field] || field;

const ActivityDiff = ({ details, collapsed = true }) => {
  const [isExpanded, setIsExpanded] = useState(!collapsed);

  if (!details) return null;

  const { type, values, changes } = details;

  // Pas de details a afficher
  if (!values && (!changes || changes.length === 0)) return null;

  const renderChanges = () => {
    if (type === 'create' && values) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {Object.entries(values).map(([field, value]) => (
            <div key={field} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px' }}>
              <span style={{ color: '#666', minWidth: '120px' }}>{getFieldLabel(field)}:</span>
              <span style={{ color: '#4caf50', fontWeight: 500 }}>{formatValue(value)}</span>
            </div>
          ))}
        </div>
      );
    }

    if (type === 'delete' && values) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {Object.entries(values).map(([field, value]) => (
            <div key={field} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px' }}>
              <span style={{ color: '#666', minWidth: '120px' }}>{getFieldLabel(field)}:</span>
              <span style={{ color: '#f44336', textDecoration: 'line-through' }}>{formatValue(value)}</span>
            </div>
          ))}
        </div>
      );
    }

    if (type === 'update' && changes && changes.length > 0) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {changes.map((change, idx) => (
            <div key={idx} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '12px',
              padding: '4px 8px',
              background: '#fff8e1',
              borderRadius: '4px'
            }}>
              <span style={{ color: '#666', fontWeight: 500, minWidth: '100px' }}>
                {getFieldLabel(change.field)}:
              </span>
              <span style={{ color: '#f44336', textDecoration: 'line-through' }}>
                {formatValue(change.from)}
              </span>
              <span style={{ color: '#666' }}>→</span>
              <span style={{ color: '#4caf50', fontWeight: 500 }}>
                {formatValue(change.to)}
              </span>
            </div>
          ))}
        </div>
      );
    }

    return null;
  };

  const changesCount = type === 'update' ? changes?.length : (values ? Object.keys(values).length : 0);

  return (
    <div style={{
      marginTop: '8px',
      borderLeft: '3px solid #e0e0e0',
      paddingLeft: '12px'
    }}>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          background: 'none',
          border: 'none',
          padding: '4px 0',
          cursor: 'pointer',
          fontSize: '12px',
          color: '#1976d2',
          display: 'flex',
          alignItems: 'center',
          gap: '4px'
        }}
      >
        <span>{isExpanded ? '▼' : '▶'}</span>
        <span>{changesCount} modification{changesCount > 1 ? 's' : ''}</span>
      </button>

      {isExpanded && (
        <div style={{ marginTop: '8px' }}>
          {renderChanges()}
        </div>
      )}
    </div>
  );
};

export default ActivityDiff;
